import { useQuery, useMutation } from "@apollo/client/react"
import { Button, CircularProgress, Alert, IconButton } from "@mui/material"
import { Formik } from "formik"
import { X } from "lucide-mui"
import React, { useState } from "react"
import * as Yup from "yup"

import {
  Role,
  GetCurrentUserWithInfoDocument,
  type GetCurrentUserWithInfoQuery,
  UpdateUserInfoDocument,
  type UpdateUserInfoMutation,
  type UpdateUserInfoMutationVariables,
} from "@/graphql/graphql"
import i18n from "@/i18n"
import { showErrorToast } from "@/shared/toast"

import AvatarSection from "./avatar_section"
import PersonalInfoSection, { type ProfileFormValues } from "./personal_info_section"
import useAvatarUpload from "./use_avatar_upload"

const STYLES = {
  loadingContainer: "flex justify-center items-center min-h-screen",
  errorText: "text-red-500 p-8",
  notFound: "p-8",
  form: "flex flex-col gap-6 max-w-4xl",
  title: "text-3xl font-bold mb-0",
  closeIcon: "w-5 h-5",
  actionsRow: "flex justify-between gap-3",
  rightActions: "flex gap-3",
} as const

const validationSchema = Yup.object({
  firstName: Yup.string(),
  lastName: Yup.string(),
  email: Yup.string().email(i18n.t("app.settings.profile.overview.errors.email_invalid")),
  phone: Yup.string(),
  position: Yup.string(),
  country: Yup.string(),
  address: Yup.string(),
  city: Yup.string(),
  postcode: Yup.string(),
})

const ProfileOverview: React.FC = () => {
  const { avatarFile, avatarPreview, handleAvatarChange, resetAvatar } = useAvatarUpload()
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const { data, loading, error, refetch } = useQuery<GetCurrentUserWithInfoQuery>(GetCurrentUserWithInfoDocument)
  const [updateUserInfoMutation, { loading: saving }] = useMutation<
    UpdateUserInfoMutation,
    UpdateUserInfoMutationVariables
  >(UpdateUserInfoDocument)

  const currentUser = data?.currentUser

  const handleRemoveAvatar = async () => {
    if (!currentUser) return

    setSuccessMessage(null)
    setErrorMessage(null)
    resetAvatar()

    try {
      const result = await updateUserInfoMutation({
        variables: {
          userId: currentUser.id,
          removeAvatar: true,
        },
      })

      if (result.data?.updateUserInfo?.success) {
        void refetch()
        setSuccessMessage(i18n.t("app.settings.profile.overview.success"))
      } else {
        const errors = result.data?.updateUserInfo?.errors
        const errorCode = errors?.[0]?.code
        const errorKey = errorCode
          ? `app.settings.profile.overview.errors.${errorCode.toLowerCase()}`
          : "app.settings.profile.overview.errors.unknown"
        setErrorMessage(i18n.t(errorKey))
      }
    } catch (err) {
      console.error("Error removing avatar:", err)
      setErrorMessage(i18n.t("app.settings.profile.overview.errors.generic"))
    }
  }

  const isAdmin = currentUser?.roles?.some((r) => r.role === Role.Admin) || false

  const handleSubmit = async (values: ProfileFormValues): Promise<void> => {
    if (!currentUser) return

    setSuccessMessage(null)
    setErrorMessage(null)

    try {
      const result = await updateUserInfoMutation({
        variables: {
          userId: currentUser.id,
          firstName: isAdmin ? values.firstName : undefined,
          lastName: isAdmin ? values.lastName : undefined,
          address: values.address,
          city: values.city,
          country: values.country,
          phone: values.phone,
          position: values.position,
          postcode: values.postcode,
          avatar: avatarFile,
        },
      })

      if (result.data?.updateUserInfo?.success) {
        resetAvatar()
        void refetch()
        setSuccessMessage(i18n.t("app.settings.profile.overview.success"))
      } else {
        const errors = result.data?.updateUserInfo?.errors
        const errorCode = errors?.[0]?.code
        const errorKey = errorCode
          ? `app.settings.profile.overview.errors.${errorCode.toLowerCase()}`
          : "app.settings.profile.overview.errors.unknown"
        setErrorMessage(i18n.t(errorKey))
      }
    } catch (err) {
      console.error("Error updating profile:", err)
      setErrorMessage(i18n.t("app.settings.profile.overview.errors.generic"))
    }
  }

  if (loading) {
    return (
      <div className={STYLES.loadingContainer}>
        <CircularProgress />
      </div>
    )
  }

  if (error) {
    return <div className={STYLES.errorText}>{i18n.t("app.settings.profile.overview.errors.load_failed")}</div>
  }

  if (!currentUser) {
    return <div className={STYLES.notFound}>{i18n.t("app.settings.profile.overview.errors.user_not_found")}</div>
  }

  const uniqueRoles = Array.from(new Set(currentUser.roles?.map((r) => r.role).filter(Boolean) || []))
  const translateRole = (role: Role): string => {
    switch (role) {
      case Role.Admin:
        return i18n.t("app.settings.users.list.roles.admin")
      case Role.Manager:
        return i18n.t("app.settings.users.list.roles.manager")
      case Role.Staff:
        return i18n.t("app.settings.users.list.roles.staff")
      default:
        return ""
    }
  }
  const rolesDisplay = uniqueRoles.map(translateRole).join(", ") || "-"

  return (
    <Formik<ProfileFormValues>
      initialValues={{
        firstName: currentUser?.firstName || "",
        lastName: currentUser?.lastName || "",
        email: currentUser?.email || "",
        phone: currentUser?.info?.phone || "",
        position: currentUser?.info?.position || "",
        country: currentUser?.info?.country || "",
        address: currentUser?.info?.address || "",
        city: currentUser?.info?.city || "",
        postcode: currentUser?.info?.postcode || "",
      }}
      validationSchema={validationSchema}
      enableReinitialize={true}
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <form onSubmit={formik.handleSubmit} className={STYLES.form} data-testid="settings-profile-form">
          <h1 className={STYLES.title} data-testid="settings-profile-title">
            {i18n.t("app.settings.profile.overview.title")}
          </h1>

          {successMessage ? (
            <Alert
              severity="success"
              data-testid="settings-profile-alert-success"
              action={
                <IconButton aria-label="close" color="inherit" size="small" onClick={() => setSuccessMessage(null)}>
                  <X className={STYLES.closeIcon} />
                </IconButton>
              }
            >
              {successMessage}
            </Alert>
          ) : null}

          {errorMessage ? (
            <Alert
              severity="error"
              data-testid="settings-profile-alert-error"
              action={
                <IconButton aria-label="close" color="inherit" size="small" onClick={() => setErrorMessage(null)}>
                  <X className={STYLES.closeIcon} />
                </IconButton>
              }
            >
              {errorMessage}
            </Alert>
          ) : null}

          <AvatarSection
            currentUser={currentUser}
            avatarPreview={avatarPreview}
            avatarFile={avatarFile}
            handleAvatarChange={handleAvatarChange}
            handleRemoveAvatar={handleRemoveAvatar}
          />

          <PersonalInfoSection isAdmin={isAdmin} rolesDisplay={rolesDisplay} />

          <div className={STYLES.actionsRow}>
            <Button
              variant="outlined"
              color="error"
              data-testid="settings-profile-btn-sign-out"
              onClick={() => {
                fetch("/auth/sign_out", {
                  method: "DELETE",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  mode: "cors",
                  credentials: "same-origin",
                })
                  .then(() => {
                    window.location.href = "/sign_in"
                    return undefined
                  })
                  .catch((error) => {
                    showErrorToast()
                    console.error(error)
                  })
              }}
            >
              {i18n.t("app.settings.profile.overview.sign_out")}
            </Button>
            <div className={STYLES.rightActions}>
              <Button
                variant="outlined"
                color="primary"
                data-testid="settings-profile-btn-cancel"
                onClick={() => {
                  formik.resetForm()
                  resetAvatar()
                }}
                disabled={saving}
              >
                {i18n.t("app.actions.cancel")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={saving || (!formik.dirty && !avatarFile)}
                data-testid="settings-profile-btn-save"
              >
                {saving ? <CircularProgress size={24} /> : i18n.t("app.settings.profile.overview.save_changes")}
              </Button>
            </div>
          </div>
        </form>
      )}
    </Formik>
  )
}

export default ProfileOverview
